"use client";

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Phone, MessageSquare, Loader2, X, Link2,
  Megaphone, Briefcase, Building2, CheckCircle
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useTabbedChrome } from '@/components/weavein';

// ── Types ───────────────────────────────────────────────────────────

interface SenderAssignment {
  id: string;
  phone_number: string;
  provider_id: string | null;
  assignment_type: string;
  assignment_id: string | null;
  channel: string;
  created_at: string | null;
}

interface OrgPhoneNumber {
  phone_number: string;
  friendly_name?: string;
  channel?: string;
  provider_type?: string;
}

interface ProgramOption {
  id: number;
  name: string;
}

interface CampaignOption {
  id: string;
  name: string;
  status: string;
}

// ── Constants ───────────────────────────────────────────────────────

const CHANNEL_BADGES: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  sms: { label: 'SMS', color: 'bg-green-100 text-green-700', icon: Phone },
  whatsapp: { label: 'WhatsApp', color: 'bg-green-50 text-green-700', icon: MessageSquare },
  rcs: { label: 'RCS', color: 'bg-indigo-100 text-indigo-700', icon: Phone },
};

const TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  organization: { label: 'Organization', icon: Building2, color: 'text-blue-600' },
  campaign: { label: 'Campaign', icon: Megaphone, color: 'text-purple-600' },
  program: { label: 'Program', icon: Briefcase, color: 'text-orange-600' },
};

// ── Component ───────────────────────────────────────────────────────

export const SenderNumberAssignments: React.FC = () => {
  const tabbed = useTabbedChrome();
  const organizationId = localStorage.getItem('active_cbo_id') ? Number(localStorage.getItem('active_cbo_id')) : null;

  // Data
  const [assignments, setAssignments] = useState<SenderAssignment[]>([]);
  const [orgPhoneNumbers, setOrgPhoneNumbers] = useState<OrgPhoneNumber[]>([]);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formPhoneNumber, setFormPhoneNumber] = useState('');
  const [formChannel, setFormChannel] = useState('sms');
  const [formAssignmentType, setFormAssignmentType] = useState('organization');
  const [formSelectedIds, setFormSelectedIds] = useState<string[]>([]);

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'X-CBO-ID': String(organizationId || ''),
  }), [organizationId]);

  // ── Fetch all data ────────────────────────────────────────────────

  const fetchAssignments = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/communication-providers/sender-assignments?organization_id=${organizationId}`),
        { headers: authHeaders() }
      );
      if (res.ok) setAssignments(await res.json());
    } catch (err) {
      console.error('Failed to fetch sender assignments:', err);
    }
  }, [organizationId, authHeaders]);

  const fetchOrgPhoneNumbers = useCallback(async () => {
    if (!organizationId) return;
    const numbers: OrgPhoneNumber[] = [];
    const seen = new Set<string>();

    const addNumber = (phone: string, friendly?: string, provType?: string, ch?: string) => {
      if (!phone || seen.has(phone)) return;
      seen.add(phone);
      numbers.push({
        phone_number: phone,
        friendly_name: friendly || phone,
        provider_type: provType,
        channel: ch,
      });
    };

    // 1. Fetch user-assigned phone numbers (e.g., BYOP per-user numbers)
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/user-phone-numbers/organization/${organizationId}`),
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        list.forEach((n: any) => {
          const phone = n.phone_number || n.phone;
          if (phone) {
            const userName = n.user_name || n.friendly_name || '';
            addNumber(phone, userName ? `${phone} (${userName})` : phone, n.provider_type || 'user-assigned');
          }
        });
      }
    } catch { /* ignore */ }

    // 2. Fetch provisioned Twilio numbers
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/phone-provisioning/organization/${organizationId}`),
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.numbers || [];
        list.forEach((n: any) => {
          addNumber(n.phone_number, n.friendly_name || n.phone_number, 'provisioned');
        });
      }
    } catch { /* ignore */ }

    // 3. Fetch communication providers (BYOP org-level)
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/communication-providers/?organization_id=${organizationId}`),
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.providers || [];
        list.forEach((p: any) => {
          addNumber(p.phone_number, `${p.phone_number} (${p.provider_type || 'provider'})`, p.provider_type, p.channel);
        });
      }
    } catch { /* ignore */ }

    // 4. Fetch resolved number (platform default fallback)
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/user-phone-numbers/resolve/${organizationId}?channel=sms`),
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.from_number) {
          addNumber(data.from_number, `${data.from_number} (${data.resolution_source || 'resolved'})`, data.resolution_source);
        }
      }
    } catch { /* ignore */ }

    setOrgPhoneNumbers(numbers);
  }, [organizationId, authHeaders]);

  const fetchPrograms = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/programs?cbo_id=${organizationId}`),
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.programs || [];
        setPrograms(list.map((p: any) => ({
          id: p.id,
          name: p.name || p.program_name || `Program ${p.id}`,
        })));
      }
    } catch (err) {
      console.error('Failed to fetch programs:', err);
    }
  }, [organizationId, authHeaders]);

  const fetchCampaigns = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/campaigns?organization_id=${organizationId}`),
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.campaigns || [];
        setCampaigns(list.map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status,
        })));
      }
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    }
  }, [organizationId, authHeaders]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchAssignments(), fetchOrgPhoneNumbers(), fetchPrograms(), fetchCampaigns()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchAssignments, fetchOrgPhoneNumbers, fetchPrograms, fetchCampaigns]);

  // ── Create assignments (supports multi-select) ────────────────────

  const createAssignments = async () => {
    if (!organizationId || !formPhoneNumber) return;
    setSaving(true);
    try {
      if (formAssignmentType === 'organization') {
        // Single assignment for org-wide
        await fetch(getWeaveinApiUrl('/api/communication-providers/sender-assignments'), {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            phone_number: formPhoneNumber,
            organization_id: organizationId,
            assignment_type: 'organization',
            assignment_id: null,
            channel: formChannel,
          }),
        });
      } else {
        // Create one assignment per selected program/campaign
        for (const id of formSelectedIds) {
          await fetch(getWeaveinApiUrl('/api/communication-providers/sender-assignments'), {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
              phone_number: formPhoneNumber,
              organization_id: organizationId,
              assignment_type: formAssignmentType,
              assignment_id: id,
              channel: formChannel,
            }),
          });
        }
      }
      setShowForm(false);
      resetForm();
      fetchAssignments();
    } catch (err) {
      console.error('Failed to create assignment:', err);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormPhoneNumber('');
    setFormChannel('sms');
    setFormAssignmentType('organization');
    setFormSelectedIds([]);
  };

  const toggleSelectedId = (id: string) => {
    setFormSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // ── Helpers ───────────────────────────────────────────────────────

  const getAssignmentLabel = (a: SenderAssignment): string => {
    if (a.assignment_type === 'organization') return 'Organization-wide';
    if (a.assignment_type === 'campaign') {
      const c = campaigns.find(x => x.id === a.assignment_id);
      return c ? c.name : `Campaign ${a.assignment_id?.slice(0, 8)}...`;
    }
    if (a.assignment_type === 'program') {
      const p = programs.find(x => String(x.id) === a.assignment_id);
      return p ? p.name : `Program ${a.assignment_id}`;
    }
    return a.assignment_id || '';
  };

  // Group assignments by phone number for display
  const groupedByPhone = assignments.reduce<Record<string, SenderAssignment[]>>((acc, a) => {
    const key = `${a.phone_number}|${a.channel}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  const canCreate = formPhoneNumber && (
    formAssignmentType === 'organization' || formSelectedIds.length > 0
  );

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Sender Number Assignments
            </h2>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Assign your organization&apos;s phone numbers to specific campaigns or programs for message routing
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> Add Assignment
        </button>
      </div>

      {/* Organization Phone Numbers Summary */}
      {orgPhoneNumbers.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-800 mb-2">
            Organization Phone Numbers ({orgPhoneNumbers.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {orgPhoneNumbers.map((n, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-md border border-blue-200 text-sm text-gray-700">
                <Phone className="w-3.5 h-3.5 text-blue-500" />
                {n.phone_number}
                {n.provider_type && (
                  <span className="text-xs text-gray-400">({n.provider_type})</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Assignment List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
        </div>
      ) : Object.keys(groupedByPhone).length === 0 ? (
        <div className="text-center py-12 text-gray-500 border border-dashed rounded-lg">
          <Phone className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No sender assignments configured</p>
          <p className="text-sm mt-1">Assign phone numbers to campaigns or programs for routing</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByPhone).map(([key, items]) => {
            const [phoneNumber, channel] = key.split('|');
            const badge = CHANNEL_BADGES[channel] || CHANNEL_BADGES.sms;
            const BadgeIcon = badge.icon;

            return (
              <div key={key} className="bg-white border rounded-lg overflow-hidden">
                {/* Phone number header */}
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-semibold text-gray-900">{phoneNumber}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                      <BadgeIcon className="w-3 h-3" /> {badge.label}
                    </span>
                    <span className="text-xs text-gray-400">{items.length} assignment{items.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Assignments for this number */}
                <div className="divide-y divide-gray-100">
                  {items.map((a) => {
                    const typeConf = TYPE_CONFIG[a.assignment_type] || TYPE_CONFIG.organization;
                    const TypeIcon = typeConf.icon;
                    return (
                      <div key={a.id} className="px-4 py-2.5 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          <TypeIcon className={`w-4 h-4 ${typeConf.color}`} />
                          <span className="text-sm text-gray-700">{getAssignmentLabel(a)}</span>
                          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                            {typeConf.label}
                          </span>
                        </div>
                        <button
                          onClick={async () => {
                            setAssignments(prev => prev.filter(x => x.id !== a.id));
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove assignment"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create Assignment Modal ──────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Assign Sender Number</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Phone Number — dropdown of org numbers or manual entry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                {orgPhoneNumbers.length > 0 ? (
                  <select
                    value={formPhoneNumber}
                    onChange={(e) => setFormPhoneNumber(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a number...</option>
                    {orgPhoneNumbers.map((n, i) => (
                      <option key={i} value={n.phone_number}>
                        {n.phone_number} {n.friendly_name && n.friendly_name !== n.phone_number ? `- ${n.friendly_name}` : ''} {n.provider_type ? `(${n.provider_type})` : ''}
                      </option>
                    ))}
                    <option value="__custom__">Enter manually...</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formPhoneNumber}
                    onChange={(e) => setFormPhoneNumber(e.target.value)}
                    placeholder="+15551234567"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )}
                {formPhoneNumber === '__custom__' && (
                  <input
                    type="text"
                    value=""
                    onChange={(e) => setFormPhoneNumber(e.target.value)}
                    placeholder="+15551234567"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mt-2"
                    autoFocus
                  />
                )}
              </div>

              {/* Channel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                <div className="flex gap-2">
                  {Object.entries(CHANNEL_BADGES).map(([ch, conf]) => {
                    const Icon = conf.icon;
                    return (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => setFormChannel(ch)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          formChannel === ch
                            ? `${conf.color} border-current`
                            : 'border-gray-300 text-gray-500 hover:border-gray-400'
                        }`}
                      >
                        <Icon className="w-4 h-4" /> {conf.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Assignment Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <div className="flex gap-2">
                  {Object.entries(TYPE_CONFIG).map(([type, conf]) => {
                    const Icon = conf.icon;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => { setFormAssignmentType(type); setFormSelectedIds([]); }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          formAssignmentType === type
                            ? 'bg-blue-50 border-blue-500 text-blue-700'
                            : 'border-gray-300 text-gray-500 hover:border-gray-400'
                        }`}
                      >
                        <Icon className="w-4 h-4" /> {conf.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Program / Campaign multi-select */}
              {formAssignmentType === 'program' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Programs <span className="text-gray-400 font-normal">(select one or more)</span>
                  </label>
                  {programs.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No programs found for this organization</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                      {programs.map((p) => {
                        const selected = formSelectedIds.includes(String(p.id));
                        return (
                          <label
                            key={p.id}
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                              selected ? 'bg-blue-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleSelectedId(String(p.id))}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <Briefcase className={`w-4 h-4 flex-shrink-0 ${selected ? 'text-blue-600' : 'text-gray-400'}`} />
                            <span className="text-sm text-gray-700 truncate">{p.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {formSelectedIds.length > 0 && (
                    <p className="text-xs text-blue-600 mt-1.5">{formSelectedIds.length} program{formSelectedIds.length !== 1 ? 's' : ''} selected</p>
                  )}
                </div>
              )}

              {formAssignmentType === 'campaign' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Campaigns <span className="text-gray-400 font-normal">(select one or more)</span>
                  </label>
                  {campaigns.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No campaigns found for this organization</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                      {campaigns.map((c) => {
                        const selected = formSelectedIds.includes(c.id);
                        return (
                          <label
                            key={c.id}
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                              selected ? 'bg-purple-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleSelectedId(c.id)}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <Megaphone className={`w-4 h-4 flex-shrink-0 ${selected ? 'text-purple-600' : 'text-gray-400'}`} />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-gray-700 truncate block">{c.name}</span>
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              c.status === 'active' ? 'bg-green-100 text-green-700' :
                              c.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {c.status}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {formSelectedIds.length > 0 && (
                    <p className="text-xs text-purple-600 mt-1.5">{formSelectedIds.length} campaign{formSelectedIds.length !== 1 ? 's' : ''} selected</p>
                  )}
                </div>
              )}

              {formAssignmentType === 'organization' && (
                <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Organization-wide assignment</p>
                    <p className="text-xs text-gray-500">This number will be used as the default sender for all outbound messages</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between items-center">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createAssignments}
                disabled={!canCreate || saving}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                {formAssignmentType === 'organization'
                  ? 'Assign to Organization'
                  : `Assign to ${formSelectedIds.length} ${formAssignmentType}${formSelectedIds.length !== 1 ? 's' : ''}`
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SenderNumberAssignments;
